import React, { useState, useEffect } from 'react';
import { string, shape as shapeType, func, number } from 'prop-types';
import {
  arrayOf,
  params,
  shape,
  string as stringType,
  customType,
  useDataModel,
  extend,
} from '@thd-nucleus/data-sources';
import { Button } from '@thd-olt-component-react/core-ui';
import { useDrawer } from '@thd-olt-component-react/drawer';
import { Input } from '@thd-olt-component-react/input';
import { Loading } from '../shared/Loading';
import { ProductInfo } from '../shared/ProductInfo';

import * as drawerStyles from '../shared/drawers.module.scss';
import * as styles from './create-quote.module.scss';
import { DRAWERS } from '../shared/Constants';

export const CreateQuoteDrawer = ({
  itemId,
  customerInfo,
  productLabel,
  price,
  brandName,
  mainImage,
  quantity,
  fulfillmentLocation,
  fulfillmentMethod,
  localStoreId,
  setError,
  setQuoteName,
  setSuccess
}) => {
  const { svocId, userId } = customerInfo;

  const [newQuoteName, setNewQuoteName] = useState('');
  const [showLoader, setShowLoader] = useState(false);
  const [
    createQuote,
    { data: createData, loading: createLoading, error: createError }
  ] = useDataModel('createQuote', {
    variables: {
      svocId,
      userId,
      lineItems: [{
        itemId,
        quantity,
        fulfillmentLocation,
        fulfillmentMethod
      }],
      localStoreId,
      cartName: newQuoteName,
    },
    ssr: false,
    refetchQueries: [
      'getQuoteList'
    ]
  });

  const { setCurrentDrawer, closeDrawer } = useDrawer();

  const createANewQuote = () => {
    setShowLoader(true);
    createQuote();
  };

  const updateQuoteName = (letter) => {
    if (/^[a-zA-Z0-9\s]*$/.test(letter)) {
      setNewQuoteName(letter);
    }
  };
  const triggerAnalytics = () => {
    const quoteId = createData?.createQuote?.quoteId;

    window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-quote.newQuoteAdd', {
      quoteType: 'online',
      quoteId,
      item: {
        quoteLocation: 'pip',
        quantity,
        price: {
          basePrice: price
        },
        productInfo: {
          sku: itemId
        }
      }
    });
  };

  useEffect(() => {
    if (createData && !createLoading && !createError) {
      setQuoteName(newQuoteName);
      setNewQuoteName('');
      setShowLoader(false);
      setSuccess(true);
      setCurrentDrawer(DRAWERS.SUCCESS);
      triggerAnalytics();
    } else if (createError) {
      setShowLoader(false);
      setError(createError);
      setCurrentDrawer(DRAWERS.ERROR);
    }
  }, [createData, createLoading, createError]);

  return (
    <div className={`${styles.createQuote} ${drawerStyles.container}`}>
      <div className={`${styles.createQuote} ${drawerStyles.content}`}>
        {showLoader && <Loading active />}
        <ProductInfo productLabel={productLabel} brandName={brandName} mainImage={mainImage} />
        <Input header="Create Quote Name:" value={newQuoteName} onChange={updateQuoteName} />
      </div>
      <div className={`${styles.createQuote} ${drawerStyles.footer}`}>
        <Button outline onClick={createANewQuote} disabled={!newQuoteName?.length}>Save</Button>
        <Button outline onClick={closeDrawer}>Cancel</Button>
      </div>
    </div>
  );
};

CreateQuoteDrawer.propTypes = {
  customerInfo: shapeType({
    svocId: string.isRequired,
    userId: string.isRequired,
  }).isRequired,
  productLabel: string.isRequired,
  price: number.isRequired,
  brandName: string,
  mainImage: shapeType({
    url: string.isRequired,
    alt: string
  }),
  itemId: string.isRequired,
  quantity: string.isRequired,
  fulfillmentLocation: string.isRequired,
  fulfillmentMethod: string.isRequired,
  localStoreId: string.isRequired,
  setError: func.isRequired,
  setQuoteName: func.isRequired,
  setSuccess: func.isRequired
};

CreateQuoteDrawer.defaultProps = {
  brandName: null,
  mainImage: {
    alt: ''
  }
};

CreateQuoteDrawer.dataModel = extend(
  {
    createQuote: params({
      svocId: stringType().isRequired(),
      userId: stringType().isRequired(),
      lineItems: arrayOf(
        customType('LineItemInput!').shape({
          itemId: stringType(),
          quantity: stringType(),
          fulfillmentLocation: stringType(),
          fulfillmentMethod: stringType()
        })
      ).isRequired(),
      localStoreId: stringType().isRequired(),
      cartName: stringType().isRequired()
    }).mutation().shape({
      quoteId: stringType(),
      errorData: arrayOf(
        shape({
          errors: arrayOf(
            shape({
              id: stringType(),
              description: stringType(),
              submessage: stringType(),
              errorCode: stringType(),
              errorProperties: shape({
                errorProperty: shape({
                  name: stringType(),
                  value: stringType()
                })
              })
            })
          ),
          httpStatus: stringType(),
          userId: stringType(),
          customerAccountId: stringType(),
          correlationId: stringType(),
          correlationType: stringType()
        })
      )
    }),
  });